import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Button from '../../components/ui/Button';
import { Field, TextInput } from '../../components/ui/Field';
import { getActiveUser, loginAdmin } from '../../actions/authAction';
import { landingPathFor } from '../../components/shell/navConfig';
import StaarvaniLogo from '../../images/staarvani.png';
import './auth.css';

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      setError('');
      setBusy(true);
      try {
        const res = await loginAdmin(dispatch, { username: email, password });

        // IDAM returns snake_case. loginAdmin only returns the payload -- persisting
        // the session is the caller's job, and axios-client reads exactly this shape.
        const token = res?.access_token;
        if (!token) {
          setError(res?.error || 'Those credentials were not recognised.');
          return;
        }
        sessionStorage.setItem('auth', JSON.stringify({ accessToken: token }));

        // Resolve the account before routing, so the landing screen is chosen from
        // real roles rather than an empty store.
        const user = await getActiveUser(dispatch, token);
        const roles = user?.data?.roles || [];
        if (roles.length === 0) {
          sessionStorage.removeItem('auth');
          setError('This account has no roles assigned. Ask a super admin to grant access.');
          return;
        }
        navigate(landingPathFor(roles), { replace: true });
      } catch (err) {
        sessionStorage.removeItem('auth');
        setError(
          err?.response?.data?.error || 'Could not sign you in. Check your details and try again.'
        );
      } finally {
        setBusy(false);
      }
    },
    [dispatch, email, password, navigate]
  );

  return (
    <div className="sv-auth">
      <aside className="sv-auth__panel">
        <img src={StaarvaniLogo} alt="Staarvani" className="sv-auth__logo" />
        <p className="sv-auth__copy">
          The operations desk behind every reading, report and partner on Staarvani.
        </p>
      </aside>

      <main className="sv-auth__main">
        <form className="sv-auth__form" onSubmit={submit}>
          <h1 className="sv-auth__title">Sign in</h1>
          <p className="sv-auth__subtitle">Use your Staarvani admin account.</p>

          {error && (
            <div className="sv-auth__banner" role="alert">
              <i className="bx bx-error-circle" aria-hidden="true" />
              {error}
            </div>
          )}

          <Field label="Email">
            <TextInput
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@staarvani.com"
              autoFocus
            />
          </Field>

          <Field label="Password">
            <TextInput
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <Link className="sv-auth__link" to="/forgot-password">
            Forgot password?
          </Link>

          <Button
            variant="primary"
            type="submit"
            className="sv-auth__submit"
            loading={busy}
            disabled={!email || !password}
          >
            Sign in
          </Button>
        </form>
      </main>
    </div>
  );
}
